import React from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PageBreadcrumbs from './PageBreadcrumbs';

/**
 * ComingSoonPage
 *
 * Lightweight placeholder for sections of the new prediction-graph
 * platform whose backend is not wired yet. The sidebar already links to
 * these routes so the information architecture can be reviewed end-to-end;
 * each route renders this page until the real feature ships.
 *
 * Replace a route's `<ComingSoonPage .../>` with the real page component
 * when the corresponding phase lands (see /plans).
 *
 * @param {string} title        Section name (also used in the breadcrumb).
 * @param {string} description  One-line explanation of what the section will do.
 * @param {React.ReactNode} icon Optional icon element shown in the header + badge.
 * @param {string} phase        Optional roadmap tag (e.g. "Roadmap · Phase 11").
 */
const ComingSoonPage = ({ title, description, icon = null, phase = null }) => {
  const headerIcon = icon || <AutoAwesomeIcon />;

  return (
    <Box sx={{ p: 0, width: '100%' }}>
      <PageBreadcrumbs
        items={[{ text: title, icon: headerIcon }]}
        mb={0.5}
      />

      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 1 }}>
          {description}
        </Typography>
      )}

      <Paper
        variant="outlined"
        sx={{
          mt: 2,
          py: 8,
          px: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          borderRadius: 2,
          borderStyle: 'dashed',
          bgcolor: 'transparent',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: 'primary.light',
            color: 'primary.main',
            mb: 2,
            '& svg': { fontSize: 34 },
          }}
        >
          {headerIcon}
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {title} is coming soon
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 480, mb: phase ? 2 : 0 }}
        >
          {description
            || 'This section is part of the upcoming Predict.ai release and is not available yet.'}
        </Typography>

        {phase && (
          <Chip label={phase} size="small" color="primary" variant="outlined" />
        )}
      </Paper>
    </Box>
  );
};

export default ComingSoonPage;
